import {
  AddressCard,
  CollapsibleCard,
  IconButton,
  TableCell,
  TableRow,
} from '@atgfr8/atg-ui-tailwind';
import { BasicModal, BasicTable, Button } from '@components';
import {
  Contact,
  customerDetailsStore,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { closeModal, openModal } from '@store/modals';
import { DeleteIcon, AccountPlusIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { AddNewContact } from '@views/order/components';
import { cloneDeep, isEmpty } from 'lodash';
import { Index, Show, createSignal } from 'solid-js';

const GeneralCustomerContact = () => {
  const [selectedContact, setSelectedContact] = createSignal<Contact | null>(
    null,
  );

  const contactModalId = 'contactModalId';
  const handleCustomerContactData = (values: Contact) => {
    const updatedValues = {
      ...values,
      phone: values.phone1,
      operationType: !Boolean(values.id) ? 'Insert' : values.operationType,
    };
    const duplicateUserName = customerDetailsStore.customer.contacts.find(
      (contact) => {
        if (
          contact.userName !== null &&
          values.userName !== null &&
          contact.userName.toLowerCase() !== '' &&
          values.userName.toLowerCase() !== ''
        ) {
          return (
            contact.userName.toLowerCase() === values.userName.toLowerCase() &&
            contact.id !== values.id
          );
        }
        return false;
      },
    );
    if (duplicateUserName) return;
    const contacts = customerDetailsStore.customer.contacts.map((contact) =>
      contact.id === values.id
        ? {
            ...contact,
            ...updatedValues,
            operationType:
              values.operationType !== 'Insert' ? 'Update' : 'Insert',
          }
        : contact,
    );

    if (!contacts.some((contact) => contact.id === values.id)) {
      contacts.push({ ...updatedValues, id: contacts.length + 1 });
    }

    updateCustomerDetailsState({ contacts });
    handleClose();
  };

  const handleDeleteContact = (id: number) => {
    const contacts = cloneDeep(customerDetailsStore.customer.contacts);
    const idx = contacts.findIndex((x) => x.id === id);
    if (contacts[idx].operationType === 'Insert') {
      contacts.splice(idx, 1);
    } else {
      contacts[idx].status = 'Inactive';
      contacts[idx].operationType = 'Update';
    }
    updateCustomerDetailsState({ contacts });
  };

  const handleClose = () => {
    closeModal(contactModalId);
    setSelectedContact(null);
  };

  const activeContacts = () =>
    customerDetailsStore.customer.contacts.filter(
      (contact) => contact.status !== 'Inactive',
    );

  return (
    <div>
      <CollapsibleCard
        startTitle="Contact"
        endIcon={
          <Button
            variant="contained"
            color="primary"
            label="New Contact"
            onClick={() => openModal(contactModalId)}
            startIcon={<AccountPlusIcon size={20} />}
            dataTestId={formatDataTestId('New Contact', 'button')}
          />
        }
        class="mb-2"
      >
        <BasicTable
          columns={[{ name: 'Name' }, { name: 'Type' }, { name: 'Delete' }]}
          tableContainerClasses="max-h-[400px] overflow-y-auto! text-black! [&_td]:!border-[#e0e0e0] [&_th]:!border-[#e0e0e0]"
          tableHeaderClasses="!sticky top-0 bg-[#f2f6f8]! shadow-xs! z-1"
        >
          <Index each={activeContacts()}>
            {(item) => (
              <TableRow class="hover:bg-[#D2E2ED]">
                <TableCell
                  class="cursor-pointer"
                  onClick={() => {
                    (setSelectedContact(item()), openModal(contactModalId));
                  }}
                >
                  <AddressCard
                    isCustomerContact={true}
                    mode="regular"
                    contact={{
                      userName: item().userName,
                      name: item().name,
                      email: item().email,
                      phone1: item().phone1,
                      extension1: item().extension1,
                      phone2: item().phone2,
                      extension2: item().extension2,
                      fax: item().fax,
                    }}
                    contactType={item().type}
                  />
                </TableCell>
                <TableCell
                  class="cursor-pointer"
                  onClick={() => {
                    (setSelectedContact(item()), openModal(contactModalId));
                  }}
                >
                  {item().type}
                </TableCell>
                <TableCell>
                  <Show when={isEmpty(item().userName)}>
                    <IconButton
                      onClick={() => handleDeleteContact(item().id)}
                      dataTestId="general-customer-contact-delete-icon-button"
                    >
                      <DeleteIcon class="text-red-700" />
                    </IconButton>
                  </Show>
                </TableCell>
              </TableRow>
            )}
          </Index>
        </BasicTable>

        <BasicModal
          id={contactModalId}
          title={`${selectedContact() ? 'Edit' : 'Add'} Contact`}
          width="70vw"
          maxWidth="600px"
          footer={false}
          onClose={() => handleClose()}
        >
          <AddNewContact
            modalId={contactModalId}
            initiatePath="CustomerDetails"
            onSubmit={(values) => handleCustomerContactData(values as Contact)}
            editObject={selectedContact}
            handleClose={handleClose}
          />
        </BasicModal>
      </CollapsibleCard>
    </div>
  );
};

export default GeneralCustomerContact;
